// src/modules/IntegratedMessaging/components/modals/EditIntegrationModal.tsx

import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
  ForwardedRef,
  FormEvent,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { PasswordInput } from '@shared/components/ui/PasswordInput';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@shared/components/ui/select';
import { Checkbox } from '@shared/components/ui/checkbox';
import { useToast } from '@shared/hooks/use-toast';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateIntegration } from '../../hooks/useUpdateIntegration';
import { useUpdateIntegrationVisibility } from '../../hooks/useUpdateIntegrationVisibility';
import { useCountries } from '../../hooks/useCountries';
import { useTeams } from '../../hooks/useTeams';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { IntegratedMessaging } from '../../models/IntegratedMessaging';

export interface EditIntegrationModalRef {
  open: (integration: IntegratedMessaging) => void;
}

export const EditIntegrationModal = forwardRef((_: {}, ref: ForwardedRef<EditIntegrationModalRef>) => {
  const { t } = useTranslation('integratedmessagingModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const mutation = useUpdateIntegration();
  const visibilityMutation = useUpdateIntegrationVisibility();
  const { data: countries = [] } = useCountries();
  const { data: teams = [] } = useTeams();
  const { getTeamType } = usePermissions();

  const [integration, setIntegration] = useState<IntegratedMessaging | null>(null);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);

  // Verificar si el usuario es 3pl
  const is3pl = getTeamType() === 'tenant_3pl';

  useImperativeHandle(ref, () => ({
    open: (integration) => setIntegration(integration),
  }));

  // Opciones para el MultiSelect de teams - debe estar antes del early return
  const teamOptions = useMemo(
    () =>
      teams.map((team) => ({
        value: team.id.toString(),
        label: team.name,
      })),
    [teams],
  );

  useEffect(() => {
    if (!integration) return;
    modalRef.current?.openModal();
    setFormValues({
      name: integration.name,
      ...integration.data,
    });
    // Cargar visibility y private_team_ids desde la integración
    setVisibility(integration.visibility === 'private' ? 'private' : 'public');
    setSelectedTeamIds((integration.private_team_ids || []).map((id) => id.toString()));
  }, [integration]);

  if (!integration) return null;

  const fields = integration.courier.columns_data;
  const isPrivate = visibility === 'private';

  const handleChange = (key: string, value: any) => setFormValues((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!integration) return;

    const hasNameOrDataChanged =
      formValues.name !== integration.name ||
      JSON.stringify(
        fields.reduce<Record<string, any>>((acc, key) => {
          acc[key] = formValues[key];
          return acc;
        }, {}),
      ) !== JSON.stringify(integration.data);

    // Comparar arrays de teams ordenados para detectar cambios
    const originalTeamIds = (integration.private_team_ids || []).map((id) => id.toString()).sort();
    const currentTeamIds = [...selectedTeamIds].sort();
    const teamsChanged = JSON.stringify(originalTeamIds) !== JSON.stringify(currentTeamIds);

    const originalVisibility = integration.visibility === 'private' ? 'private' : 'public';
    const visibilityChanged = visibility !== originalVisibility;

    const hasVisibilityChanged = is3pl && (visibilityChanged || teamsChanged);

    // Debug: Log para verificar detección de cambios
    console.log('🔍 EditIntegrationModal - Cambios detectados:', {
      hasNameOrDataChanged,
      hasVisibilityChanged,
      visibilityChanged,
      teamsChanged,
      originalTeamIds,
      currentTeamIds,
      originalVisibility,
      currentVisibility: visibility,
      is3pl,
    });

    try {
      // Actualizar name y data si cambiaron
      if (hasNameOrDataChanged) {
        await mutation.mutateAsync({
          id: integration.id,
          name: formValues.name === integration.name ? null : formValues.name,
          data: fields.reduce<Record<string, any>>((acc, key) => {
            acc[key] = formValues[key];
            return acc;
          }, {}),
        });
      }

      // Actualizar visibilidad si cambió (solo para usuarios 3pl)
      if (hasVisibilityChanged) {
        await visibilityMutation.mutateAsync({
          id: integration.id,
          visibility,
          private_team_ids: selectedTeamIds.length > 0 ? selectedTeamIds.map((id) => parseInt(id, 10)) : [],
        });
      }

      // Si no hubo cambios, mostrar mensaje
      if (!hasNameOrDataChanged && !hasVisibilityChanged) {
        toast({
          title: t('messages.updated'),
          description: 'No se detectaron cambios',
        });
      } else {
        toast({ title: t('messages.updated') });
      }

      modalRef.current?.closeModal();
      queryClient.invalidateQueries({
        queryKey: ['courier-integrations'],
      });
    } catch (err: any) {
      toast({
        title: t('messages.update_error'),
        description: err?.message || 'Error desconocido',
        variant: 'destructive',
      });
    }
  };

  return (
    <ModalCenter
      ref={modalRef}
      onCloseModal={() => setIntegration(null)}
      title={t('modal.edit.title', { name: integration.name })}
      type="default"
      width="lg"
      className="h-[calc(100%-30px)] sm:h-auto"
    >
      <form onSubmit={handleSubmit} autoComplete="off" className="space-y-4 flex flex-col justify-between min-h-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="text-sm font-medium text-text mb-1 block">{t('modal.fields.name_label')}</label>
            <Input
              value={formValues.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('modal.fields.name_placeholder')}
            />
          </div>

          {fields.map((key, idx) => {
            const lc = key.toLowerCase();
            const label = t(`modal.fields.${lc}_label`, {
              defaultValue: key,
            });
            const placeholder = t(`modal.fields.${lc}_placeholder`, {
              defaultValue: '',
            });

            if (lc === 'sender_country_code') {
              const isLastOdd = idx === fields.length - 1 && fields.length % 2 === 1;
              return (
                <div key={key} className={isLastOdd ? 'sm:col-span-2' : ''}>
                  <label className="text-sm font-medium text-text mb-1 block">{label}</label>
                  <Select value={formValues[key] ?? ''} onValueChange={(val) => handleChange(key, val)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => (
                        <SelectItem key={c.iso2} value={c.iso2}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            }

            const FieldComponent = lc.includes('password') ? PasswordInput : Input;
            const isLastOdd = idx === fields.length - 1 && fields.length % 2 === 1;

            return (
              <div key={key} className={isLastOdd ? 'sm:col-span-2' : ''}>
                <label className="text-sm font-medium text-text mb-1 block">{label}</label>
                <FieldComponent
                  value={formValues[key] ?? ''}
                  onChange={(e) => handleChange(key, e.target.value)}
                  placeholder={placeholder}
                  autoComplete={lc.includes('password') ? 'new-password' : undefined}
                />
              </div>
            );
          })}

          {/* Campos adicionales para usuarios 3pl */}
          {is3pl && (
            <>
              {/* Checkbox de visibilidad privada */}
              <div className="sm:col-span-1">
                <label className="text-sm font-medium text-text mb-1 block">
                  {t('modal.fields.visibility_label', {
                    defaultValue: 'Visibilidad',
                  })}
                </label>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="visibility-private-edit"
                    checked={isPrivate}
                    onCheckedChange={(checked) => setVisibility(checked ? 'private' : 'public')}
                    data-testid="visibility-checkbox-edit"
                  />
                  <label
                    htmlFor="visibility-private-edit"
                    className="text-xs font-medium text-complementary-text cursor-pointer pt-1"
                  >
                    {t('modal.fields.visibility_private_label', {
                      defaultValue: 'Privada',
                    })}
                  </label>
                </div>
              </div>

              {/* Selector múltiple de teams (solo si es privada) */}
              {isPrivate && (
                <div className="sm:col-span-1">
                  <label className="text-sm font-medium text-text mb-1 block">
                    {t('modal.fields.teams_label', {
                      defaultValue: 'Teams',
                    })}
                  </label>
                  <MultiSelect
                    options={teamOptions}
                    value={selectedTeamIds}
                    onValueChange={setSelectedTeamIds}
                    placeholder={t('modal.fields.teams_placeholder', {
                      defaultValue: 'Seleccionar teams...',
                    })}
                    emptyText={t('modal.fields.teams_empty', {
                      defaultValue: 'No hay teams disponibles',
                    })}
                    maxDisplay={3}
                    className="w-full bg-card-bg"
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" size="lg" type="button" onClick={() => modalRef.current?.closeModal()}>
            {t('button.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={mutation.isPending || visibilityMutation.isPending}
          >
            {t('button.save')}
          </Button>
        </div>
      </form>
    </ModalCenter>
  );
});

EditIntegrationModal.displayName = 'EditIntegrationModal';
